/**
 * 兵庫県の背景ツアー「兵庫めぐり」コンテンツ。
 *
 * 軸は季節。5月の明石海峡大橋から始めて、1年かけて翌年4月の神戸港へ戻る。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 竹田城跡を「日本のマチュピチュ」と書かない。雲海が必ず見られるとも書かない。
 *  - 鳴門の渦潮を「世界遺産」と書かない（登録を目指す取組みの段階）。
 *  - 城崎温泉の外湯は「七つ」ではなく六つ。
 *  - 辰鼓楼を「日本最古／二番目に古い時計台」と書かない（豊岡市公式に記載がない）。
 *  - 余部を「今も鉄橋の上を列車が走る」と書かない。架け替え済み。
 *  - 掬星台の「日本三大夜景」は神戸市公式観光サイトの表記としてのみ、留保付きで使う。
 *  - 姫路城の国宝は天守だけではない（大天守・小天守3・渡櫓4の計8棟）。
 */

import type { StageTour } from '../types';

export const hyogoSceneryTour: StageTour = {
  scenes: [
    {
      id: 'akashi-kaikyo-ohashi',
      name: '明石海峡大橋',
      area: '神戸市・淡路市・5月の朝',
      image: '/assets/scenery/hyogo/scenes/01_akashi_kaikyo_ohashi.webp',
      hotspots: [
        {
          id: 'akashi-shutou',
          x: 0.5,
          y: 0.34,
          title: '主塔は海面から約300メートル',
          body: '3径間2ヒンジ補剛トラス吊橋で、全長3,911メートル、中央の支間は1,991メートル。主塔の高さは海面上およそ300メートルあります。',
          source:
            'https://www.jb-honshi.co.jp/corp_index/technology/introduction/introduction_akashi.html',
          mapQuery: '明石海峡大橋',
        },
        {
          id: 'akashi-kyoudai',
          x: 0.3,
          y: 0.38,
          title: 'デンマークの橋と姉妹橋',
          body: '昭和61年4月26日に着工し、平成10年4月5日に開通しました。デンマークのグレートベルト・イースト橋と姉妹橋です。',
          source:
            'https://www.jb-honshi.co.jp/corp_index/technology/introduction/introduction_akashi.html',
          relatedItemId: 'akashi-dako',
          mapQuery: '舞子公園',
        },
      ],
    },
    {
      id: 'naruto-uzushio',
      name: '鳴門の渦潮',
      area: '南あわじ市・6月の午前',
      image: '/assets/scenery/hyogo/scenes/02_naruto_uzushio.webp',
      hotspots: [
        {
          id: 'uzushio-ookisa',
          x: 0.48,
          y: 0.5,
          title: '大潮のときは直径20〜30メートル',
          body: '大潮のときには直径20〜30メートルの大きな渦ができます。潮の流れは普段でも時速13〜15キロ、春と秋の大潮では時速20キロともいわれます。',
          source: 'https://www.city.minamiawaji.hyogo.jp/soshiki/uzushio/gaiyou.html',
          mapQuery: '鳴門の渦潮 淡路島',
        },
        {
          id: 'uzushio-shikumi',
          x: 0.64,
          y: 0.36,
          title: 'なぜ渦ができるのか',
          body: '鳴門海峡は幅およそ1.3キロ、いちばん深いところで80メートル。瀬戸内海側と太平洋側の潮位差は最大1.5メートルになり、中央の速い流れと陸地側の遅い流れの差が回転する力を生みます。',
          source: 'https://www.city.minamiawaji.hyogo.jp/soshiki/uzushio/shikumi.html',
          mapQuery: '大鳴門橋',
        },
      ],
    },
    {
      id: 'amarube',
      name: '余部鉄橋「空の駅」',
      area: '香美町・7月の昼',
      image: '/assets/scenery/hyogo/scenes/03_amarube.webp',
      hotspots: [
        {
          id: 'amarube-kyokyou',
          x: 0.48,
          y: 0.42,
          title: '旧鉄橋の橋脚を残す',
          body: '旧余部鉄橋は明治45年1月に完成した鋼トレッスル橋です。昭和61年12月の列車転落事故を契機に架け替えられ、平成22年8月に新しい橋になりました。餘部駅側の3橋脚3スパンは、展望施設として残されています。',
          source: 'https://web.pref.hyogo.lg.jp/ks05/soranoeki.html',
          mapQuery: '余部鉄橋 空の駅',
        },
        {
          id: 'amarube-elevator',
          x: 0.75,
          y: 0.3,
          title: '地上から45秒で上がれる',
          body: '餘部駅側の3橋脚3スパンを展望施設として残し、平成25年5月に完成しました。平成29年11月にエレベーター（愛称・余部クリスタルタワー）が付き、地上からおよそ40メートルの展望施設まで約45秒。利用は無料です。',
          source: 'https://www.town.mikata-kami.lg.jp/soshiki/shogaigakushu/7/712.html',
          mapQuery: '余部クリスタルタワー',
        },
      ],
    },
    {
      id: 'himejijo',
      name: '姫路城',
      area: '姫路市・8月の昼',
      image: '/assets/scenery/hyogo/scenes/04_himejijo.webp',
      hotspots: [
        {
          id: 'himeji-tenshu',
          x: 0.5,
          y: 0.32,
          title: '5重7階の連立式天守',
          body: '1601年に池田輝政が城の大改築を始め、1609年（慶長14年）に5重7階の連立式天守が完成しました。',
          source: 'https://www.city.himeji.lg.jp/castle/0000007750.html',
          mapQuery: '姫路城',
        },
        {
          id: 'himeji-kokuhou',
          x: 0.76,
          y: 0.5,
          title: '国宝は8棟',
          body: '国宝は大天守・西小天守・乾小天守・東小天守と、イ・ロ・ハ・ニの渡櫓の計8棟（附として台所1棟）。平成5年（1993年）12月11日、法隆寺地域の仏教建造物とともに、日本で初めての世界文化遺産に登録されました。',
          source: 'https://www.city.himeji.lg.jp/kanko/0000001645.html',
          relatedItemId: 'himeji-oden',
          mapQuery: '姫路城 大天守',
        },
      ],
    },
    {
      id: 'izushi-shinkoro',
      name: '出石の辰鼓楼',
      area: '豊岡市・9月の午後',
      image: '/assets/scenery/hyogo/scenes/05_izushi_shinkoro.webp',
      hotspots: [
        {
          id: 'shinkoro-taiko',
          x: 0.5,
          y: 0.34,
          title: 'もとは太鼓で時を知らせた',
          body: '明治4年、旧三の丸大手門わきの櫓台に建てられた鼓楼です。当時は1時間ごとに太鼓を打って時（辰）を告げていました。いまは大きな文字盤の時計台。',
          source: 'https://www.city.toyooka.lg.jp/1019810/1019854/1019861/1002167.html',
          mapQuery: '出石 辰鼓楼',
        },
        {
          id: 'shinkoro-jokamachi',
          x: 0.24,
          y: 0.54,
          title: '城下町ごと保存地区',
          body: '周囲の出石は、平成19年12月4日に国の重要伝統的建造物群保存地区（城下町、約23.1ヘクタール）に選ばれました。慶長9年ごろの出石城築城で発展し、文化7年の城下町絵図の姿をよく保っています。',
          source: 'https://www.city.toyooka.lg.jp/1019810/1019854/1019859/1001420/1032389.html',
          mapQuery: '出石城下町',
        },
      ],
    },
    {
      id: 'takedajo',
      name: '竹田城跡',
      area: '朝来市・10月の夜明け',
      image: '/assets/scenery/hyogo/scenes/06_takedajo.webp',
      hotspots: [
        {
          id: 'takeda-ishigaki',
          x: 0.48,
          y: 0.44,
          title: '穴太衆の野面積み',
          body: '竹田城跡の石垣は、大小さまざまな石を組み合わせる「野面積み」という技法で積まれています。近江の穴太衆と呼ばれる石工集団が築いたといわれ、天守台の隅は算木積みになっています。',
          source: 'https://www.city.asago.hyogo.jp/site/takeda/3094.html',
          mapQuery: '竹田城跡',
        },
        {
          id: 'takeda-unkai',
          x: 0.76,
          y: 0.26,
          title: '敵を防ぐ虎口',
          body: '城跡には、枡形虎口や食い違い虎口など、敵を防ぐための構造が各所に配置されています。',
          source: 'https://www.city.asago.hyogo.jp/site/takeda/3094.html',
          mapQuery: '立雲峡',
        },
        {
          id: 'takeda-kisei',
          x: 0.3,
          y: 0.55,
          title: '禁煙、トイレもない',
          body: '城跡内と城跡周辺は禁煙で、城跡内にトイレはありません。お弁当などの飲食は、入ってすぐの広い北千畳でと決められています。決められた入城時間以外は入れません。',
          source: 'https://www.city.asago.hyogo.jp/site/takeda/3095.html',
          mapQuery: '竹田城跡 山城の郷',
        },
      ],
    },
    {
      id: 'arima-onsen',
      name: '有馬温泉',
      area: '神戸市・11月の夕',
      image: '/assets/scenery/hyogo/scenes/07_arima_onsen.webp',
      hotspots: [
        {
          id: 'arima-kinsen',
          x: 0.48,
          y: 0.44,
          title: '金泉と銀泉',
          body: '褐色で高温の「金泉」と、炭酸泉とラジウム泉が混ざった無色透明の「銀泉」の2種類があります。同じ温泉地で、まったく違う湯が湧きます。',
          source: 'https://www.city.kobe.lg.jp/a64051/arimasengen.html',
          mapQuery: '有馬温泉',
        },
        {
          id: 'arima-gensen',
          x: 0.76,
          y: 0.28,
          title: '泉源は温泉街のなかにある',
          body: '天神泉源は99.4℃で深さ206メートル、妬泉源は100.5℃で187メートル、炭酸泉源は18.3℃で13メートル。奈良時代に行基菩薩が温泉寺を建て、豊臣秀吉が改修と「湯山御殿」の建設で栄えさせました。',
          source: 'https://www.city.kobe.lg.jp/a64051/arimasengen.html',
          mapQuery: '有馬温泉 天神泉源',
        },
      ],
    },
    {
      id: 'kikuseidai',
      name: '摩耶山 掬星台',
      area: '神戸市・12月の夜',
      image: '/assets/scenery/hyogo/scenes/08_kikuseidai.webp',
      hotspots: [
        {
          id: 'kikuseidai-yakei',
          x: 0.5,
          y: 0.46,
          title: '標高702メートルからの夜景',
          body: '摩耶山上、標高702メートルにある展望台です。大阪から神戸にかけてのパノラマが広がり、その夜景は日本三大夜景の一つとされ、「1,000万ドルの夜景」とも称されています。',
          source: 'https://www.feel-kobe.jp/facilities/0000000139/',
          mapQuery: '摩耶山 掬星台',
        },
        {
          id: 'kikuseidai-access',
          x: 0.24,
          y: 0.64,
          title: 'まやビューラインで上がる',
          body: 'ケーブルとロープウェイを乗りついで上る「まやビューライン」の星の駅を降りてすぐです。',
          source: 'https://www.feel-kobe.jp/kobe-yakei/spots/maya-viewline-kikuseidai/',
          mapQuery: 'まやビューライン 星の駅',
        },
      ],
    },
    {
      id: 'kinosaki-onsen',
      name: '城崎温泉',
      area: '豊岡市・1月の夜',
      image: '/assets/scenery/hyogo/scenes/09_kinosaki_onsen.webp',
      hotspots: [
        {
          id: 'kinosaki-sotoyu',
          x: 0.48,
          y: 0.44,
          title: '外湯は六つ',
          body: '鴻の湯・まんだら湯・御所の湯・一の湯・柳湯・地蔵湯の6つ。泉質はナトリウム・カルシウム－塩化物・高温泉で42℃です。1日券「ゆめぱ」ですべての外湯に入れます。',
          source: 'https://kinosaki-spa.gr.jp/about/spa/',
          mapQuery: '城崎温泉',
        },
        {
          id: 'kinosaki-rekishi',
          x: 0.76,
          y: 0.28,
          title: '開湯は720年と伝わる',
          body: '717年に城崎へ来た僧・道智上人が四所明神の神託により千日の修行を行い、720年に温泉が湧いて城崎温泉が開かれたとされます。2020年が開湯1300年でした。',
          source: 'https://kinosaki-spa.gr.jp/about/history/',
          relatedItemId: 'matsuba-gani',
          mapQuery: '城崎温泉 一の湯',
        },
      ],
    },
    {
      id: 'merikenpark',
      name: 'メリケンパーク',
      area: '神戸市・4月の朝',
      image: '/assets/scenery/hyogo/scenes/10_merikenpark.webp',
      hotspots: [
        {
          id: 'meriken-porttower',
          x: 0.5,
          y: 0.36,
          title: '鼓の形をした赤い塔',
          body: '神戸ポートタワーは高さ108メートル、1963年の竣工です。2014年12月19日に国の登録有形文化財（建造物）となりました。',
          source: 'https://online.bunka.go.jp/heritages/detail/244715',
          mapQuery: '神戸ポートタワー',
        },
        {
          id: 'meriken-hakubutsukan',
          x: 0.76,
          y: 0.5,
          title: '帆をイメージした大屋根',
          body: 'メリケンパークに建つ神戸海洋博物館は、大海原をかける帆船の帆と白い波をイメージした大屋根が目印です。夜はその建築意匠が浮かびあがり、神戸ポートタワーとの色のコントラストも見どころです。',
          source: 'https://www.feel-kobe.jp/kobe-yakei/spots/kobe-maritime-museum/',
          mapQuery: 'メリケンパーク 神戸',
        },
      ],
    },
  ],
};
